import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode, FlowNodeType } from '../../../types/flowBuilder';
import { 
  BoltIcon, 
  ClockIcon, 
  DocumentTextIcon, 
  EnvelopeIcon, 
  DocumentPlusIcon, 
  CircleStackIcon, 
  PlusCircleIcon 
} from '@heroicons/react/24/outline';
import { getNodeColors } from '../utils/nodeColors';

const getTriggerIcon = (nodeType: FlowNodeType) => {
  const iconMap = {
    trigger: BoltIcon,
    webhook_trigger: BoltIcon,
    schedule_trigger: ClockIcon,
    form_trigger: DocumentTextIcon,
    email_trigger: EnvelopeIcon,
    file_trigger: DocumentPlusIcon,
    database_trigger: CircleStackIcon,
    manual_trigger: PlusCircleIcon,
  };
  return iconMap[nodeType as keyof typeof iconMap] || BoltIcon;
};

const renderConfigPreview = (nodeType: FlowNodeType, config: any) => {
  if (!config) return null;

  const colors = getNodeColors(nodeType);

  switch (nodeType) {
    case 'trigger':
    case 'webhook_trigger':
      return config.method ? (
        <div className={`text-xs ${colors.text.secondary} mt-1`}>
          {config.method} {config.path || '/webhook'}
        </div>
      ) : null;
    
    case 'schedule_trigger':
      return config.cron ? (
        <div className={`text-xs ${colors.text.secondary} mt-1`}>
          {config.cron}
        </div>
      ) : null;
    
    case 'form_trigger':
      return config.formId ? (
        <div className={`text-xs ${colors.text.secondary} mt-1`}>
          Form: {config.formId}
        </div>
      ) : null;
    
    case 'email_trigger':
      return (
        <div className={`text-xs ${colors.text.secondary} mt-1`}>
          {config.mailbox || 'inbox'} {config.filter && `(${config.filter})`}
        </div>
      );
    
    case 'file_trigger':
      return (
        <div className={`text-xs ${colors.text.secondary} mt-1`}>
          {config.folder || '/uploads'} ({config.fileTypes?.join(', ') || '*'})
        </div>
      );
    
    case 'database_trigger':
      return config.table ? (
        <div className={`text-xs ${colors.text.secondary} mt-1`}>
          {config.table} ({config.operation || 'insert'})
        </div>
      ) : null;
    
    case 'manual_trigger':
      return (
        <div className={`text-xs ${colors.text.secondary} mt-1`}>
          {config.buttonText || 'Indítás'} {config.requireConfirmation && '(megerősítés)'}
        </div>
      );
    
    default:
      return null;
  }
};

const TriggerNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected, type }) => {
  const nodeType = (type as FlowNodeType) || 'trigger';
  const colors = getNodeColors(nodeType);
  const IconComponent = getTriggerIcon(nodeType);
  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r ${colors.gradient.from} ${colors.gradient.to} border-2 transition-all duration-200 ${
      selected ? `${colors.border.selected} shadow-xl scale-105` : colors.border.default
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <IconComponent className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-semibold ${colors.text.primary} truncate`}>
            {data.label}
          </div>
          {data.description && (
            <div className={`text-xs ${colors.text.secondary} truncate mt-1`}>
              {data.description}
            </div>
          )}
          
          {/* Config preview */}
          {renderConfigPreview(nodeType, data.config)}
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Execution data preview */}
      {data.executionData && (
        <div className={`mt-2 text-xs ${colors.text.secondary} bg-black bg-opacity-20 px-2 py-1 rounded`}>
          <div className="font-mono">
            {typeof data.executionData === 'object' 
              ? JSON.stringify(data.executionData, null, 2).substring(0, 50) + '...'
              : String(data.executionData).substring(0, 50)
            }
          </div>
        </div>
      )}

      {/* Output handle - csak trigger node-oknak van kimenete */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className={`w-3 h-3 bg-white border-2 ${colors.handle}`}
        style={{ right: -6 }}
      />
    </div>
  );
};

export default TriggerNode;
